/**
 * Client for POST /api/cognitory/v2/provisioning-setup/corpus-bootstrap plan/execute.
 */

import {
  COGNITORY_V2_PROVISIONING_CORPUS_BOOTSTRAP_PATH,
  resolveCognitoryApiBaseUrl,
} from "./cognitoryApiBase";
import { resolveCognitoryBearerToken } from "./cognitoryBearerToken";
import { parseNonemptyRequestId } from "./cognitoryProvisioningLibraryStateClient";

export type ProvisioningCorpusBootstrapPlan = {
  systemId: string;
  corpusId: string;
  corpusVersionId: string;
  namespace: string;
  pineconeIndex: string;
  planTimestamp: string;
  writeSetHash: string;
  maxWrites: 2;
};

export type ProvisioningCorpusBootstrapSummary = {
  systemId: string;
  corpusId: string;
  corpusVersionId: string;
  firestoreWriteCount: 0 | 1 | 2;
};

export type ProvisioningCorpusBootstrapPlanResult =
  | {
      kind: "planned";
      requestId: string;
      plan: ProvisioningCorpusBootstrapPlan;
    }
  | {
      kind:
        | "unauthenticated"
        | "forbidden"
        | "validation_failed"
        | "system_not_eligible"
        | "temporary_failure"
        | "unable_to_verify";
      requestId: string | null;
    };

export type ProvisioningCorpusBootstrapExecuteResult =
  | {
      kind: "bootstrapped" | "already_bootstrapped";
      requestId: string;
      bootstrap: ProvisioningCorpusBootstrapSummary;
    }
  | {
      kind:
        | "unauthenticated"
        | "forbidden"
        | "validation_failed"
        | "system_not_eligible"
        | "bootstrap_state_drift"
        | "partial_bootstrap_state"
        | "temporary_failure"
        | "unable_to_verify";
      requestId: string | null;
    };

function hasExactKeys(
  record: Record<string, unknown>,
  allowed: readonly string[],
): boolean {
  const keys = Object.keys(record);
  if (keys.length !== allowed.length) {
    return false;
  }
  for (const key of allowed) {
    if (!Object.prototype.hasOwnProperty.call(record, key)) {
      return false;
    }
  }
  return true;
}

function isFirestoreWriteCount(
  value: unknown,
): value is ProvisioningCorpusBootstrapSummary["firestoreWriteCount"] {
  return value === 0 || value === 1 || value === 2;
}

function parseBootstrapPlan(
  value: unknown,
  cognitoryId: string,
): ProvisioningCorpusBootstrapPlan | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const record = value as Record<string, unknown>;
  const allowed = [
    "systemId",
    "corpusId",
    "corpusVersionId",
    "namespace",
    "pineconeIndex",
    "planTimestamp",
    "writeSetHash",
    "maxWrites",
  ];
  if (!hasExactKeys(record, allowed)) {
    return null;
  }
  if (
    typeof record.systemId !== "string" ||
    record.systemId !== cognitoryId ||
    typeof record.corpusId !== "string" ||
    record.corpusId.length === 0 ||
    typeof record.corpusVersionId !== "string" ||
    record.corpusVersionId.length === 0 ||
    typeof record.namespace !== "string" ||
    record.namespace.length === 0 ||
    typeof record.pineconeIndex !== "string" ||
    record.pineconeIndex.length === 0 ||
    typeof record.planTimestamp !== "string" ||
    record.planTimestamp.length === 0 ||
    typeof record.writeSetHash !== "string" ||
    record.writeSetHash.length === 0 ||
    record.maxWrites !== 2
  ) {
    return null;
  }
  return {
    systemId: record.systemId,
    corpusId: record.corpusId,
    corpusVersionId: record.corpusVersionId,
    namespace: record.namespace,
    pineconeIndex: record.pineconeIndex,
    planTimestamp: record.planTimestamp,
    writeSetHash: record.writeSetHash,
    maxWrites: 2,
  };
}

function parseBootstrapSummary(
  value: unknown,
  cognitoryId: string,
): ProvisioningCorpusBootstrapSummary | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (
    !hasExactKeys(record, [
      "systemId",
      "corpusId",
      "corpusVersionId",
      "firestoreWriteCount",
    ])
  ) {
    return null;
  }
  if (
    typeof record.systemId !== "string" ||
    record.systemId !== cognitoryId ||
    typeof record.corpusId !== "string" ||
    record.corpusId.length === 0 ||
    typeof record.corpusVersionId !== "string" ||
    record.corpusVersionId.length === 0 ||
    !isFirestoreWriteCount(record.firestoreWriteCount)
  ) {
    return null;
  }
  return {
    systemId: record.systemId,
    corpusId: record.corpusId,
    corpusVersionId: record.corpusVersionId,
    firestoreWriteCount: record.firestoreWriteCount,
  };
}

async function postCorpusBootstrap(
  body: Record<string, unknown>,
  input?: {
    apiBaseUrl?: string;
    bearerToken?: string | null;
    fetchImpl?: typeof fetch;
  },
): Promise<{ status: number; body: unknown }> {
  const fetchImpl = input?.fetchImpl ?? fetch;
  const token =
    input?.bearerToken !== undefined
      ? input.bearerToken
      : await resolveCognitoryBearerToken();
  if (!token) {
    return { status: 401, body: null };
  }

  const base = input?.apiBaseUrl ?? resolveCognitoryApiBaseUrl();
  try {
    const response = await fetchImpl(
      `${base}${COGNITORY_V2_PROVISIONING_CORPUS_BOOTSTRAP_PATH}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        cache: "no-store",
        body: JSON.stringify(body),
      },
    );
    let parsed: unknown = null;
    try {
      parsed = await response.json();
    } catch {
      parsed = null;
    }
    return { status: response.status, body: parsed };
  } catch {
    return { status: 503, body: null };
  }
}

function parseStandardDenial(
  status: number,
  body: unknown,
  expectedStatus: number,
  expectedDecision: string,
): { kind: string; requestId: string } | null {
  if (status !== expectedStatus || !body || typeof body !== "object") {
    return null;
  }
  const record = body as Record<string, unknown>;
  if (!hasExactKeys(record, ["decision", "requestId"])) {
    return null;
  }
  if (record.decision !== expectedDecision) {
    return null;
  }
  const requestId = parseNonemptyRequestId(record.requestId);
  if (!requestId) {
    return null;
  }
  return { kind: expectedDecision, requestId };
}

function mapPlanDenial(
  status: number,
  body: unknown,
): ProvisioningCorpusBootstrapPlanResult {
  if (status === 401) {
    const d = parseStandardDenial(status, body, 401, "unauthenticated");
    return d
      ? { kind: "unauthenticated", requestId: d.requestId }
      : { kind: "unable_to_verify", requestId: null };
  }
  if (status === 403) {
    const d = parseStandardDenial(status, body, 403, "forbidden");
    return d
      ? { kind: "forbidden", requestId: d.requestId }
      : { kind: "unable_to_verify", requestId: null };
  }
  if (status === 400) {
    const validation = parseStandardDenial(
      status,
      body,
      400,
      "validation_failed",
    );
    if (validation) {
      return { kind: "validation_failed", requestId: validation.requestId };
    }
    const ineligible = parseStandardDenial(
      status,
      body,
      400,
      "system_not_eligible",
    );
    if (ineligible) {
      return { kind: "system_not_eligible", requestId: ineligible.requestId };
    }
    return { kind: "unable_to_verify", requestId: null };
  }
  if (status === 503) {
    const d = parseStandardDenial(status, body, 503, "temporary_failure");
    return d
      ? { kind: "temporary_failure", requestId: d.requestId }
      : { kind: "unable_to_verify", requestId: null };
  }
  return { kind: "unable_to_verify", requestId: null };
}

function mapExecuteDenial(
  status: number,
  body: unknown,
): ProvisioningCorpusBootstrapExecuteResult {
  if (status === 409) {
    const drift = parseStandardDenial(
      status,
      body,
      409,
      "bootstrap_state_drift",
    );
    if (drift) {
      return { kind: "bootstrap_state_drift", requestId: drift.requestId };
    }
    const partial = parseStandardDenial(
      status,
      body,
      409,
      "partial_bootstrap_state",
    );
    if (partial) {
      return {
        kind: "partial_bootstrap_state",
        requestId: partial.requestId,
      };
    }
    return { kind: "unable_to_verify", requestId: null };
  }
  const planStyle = mapPlanDenial(status, body);
  if (planStyle.kind === "unable_to_verify") {
    return { kind: "unable_to_verify", requestId: planStyle.requestId };
  }
  return planStyle as ProvisioningCorpusBootstrapExecuteResult;
}

export async function planProvisioningCorpusBootstrap(input: {
  cognitoryId: string;
  apiBaseUrl?: string;
  bearerToken?: string | null;
  fetchImpl?: typeof fetch;
}): Promise<ProvisioningCorpusBootstrapPlanResult> {
  const { status, body } = await postCorpusBootstrap(
    { phase: "plan", cognitoryId: input.cognitoryId },
    input,
  );

  if (status === 200) {
    if (!body || typeof body !== "object") {
      return { kind: "unable_to_verify", requestId: null };
    }
    const record = body as Record<string, unknown>;
    if (
      !hasExactKeys(record, ["decision", "requestId", "plan"]) ||
      record.decision !== "corpus_bootstrap_planned"
    ) {
      return { kind: "unable_to_verify", requestId: null };
    }
    const requestId = parseNonemptyRequestId(record.requestId);
    if (!requestId) {
      return { kind: "unable_to_verify", requestId: null };
    }
    const plan = parseBootstrapPlan(record.plan, input.cognitoryId);
    if (!plan) {
      return { kind: "unable_to_verify", requestId: null };
    }
    return { kind: "planned", requestId, plan };
  }

  return mapPlanDenial(status, body);
}

export async function executeProvisioningCorpusBootstrap(input: {
  cognitoryId: string;
  planTimestamp: string;
  writeSetHash: string;
  apiBaseUrl?: string;
  bearerToken?: string | null;
  fetchImpl?: typeof fetch;
}): Promise<ProvisioningCorpusBootstrapExecuteResult> {
  const { status, body } = await postCorpusBootstrap(
    {
      phase: "execute",
      cognitoryId: input.cognitoryId,
      planTimestamp: input.planTimestamp,
      writeSetHash: input.writeSetHash,
      confirmCorpusBootstrap: true,
      confirmCreateIfAbsentOnly: true,
    },
    input,
  );

  if (status === 200) {
    if (!body || typeof body !== "object") {
      return { kind: "unable_to_verify", requestId: null };
    }
    const record = body as Record<string, unknown>;
    if (
      !hasExactKeys(record, ["decision", "requestId", "bootstrap"]) ||
      (record.decision !== "corpus_bootstrapped" &&
        record.decision !== "corpus_bootstrap_already_bootstrapped")
    ) {
      return { kind: "unable_to_verify", requestId: null };
    }
    const requestId = parseNonemptyRequestId(record.requestId);
    if (!requestId) {
      return { kind: "unable_to_verify", requestId: null };
    }
    const bootstrap = parseBootstrapSummary(
      record.bootstrap,
      input.cognitoryId,
    );
    if (!bootstrap) {
      return { kind: "unable_to_verify", requestId: null };
    }
    return {
      kind:
        record.decision === "corpus_bootstrapped"
          ? "bootstrapped"
          : "already_bootstrapped",
      requestId,
      bootstrap,
    };
  }

  return mapExecuteDenial(status, body);
}
